import { Code, Brain, Users, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from "react";
import axios from "axios";
import host from "../../global";
import { toast } from "react-toastify";
import Loader from "../Loader.jsx";
import LimitExceededModal from "../shared_components/LimitExceededModal";

const TestLibrary = () => {
  const navigate = useNavigate();
  const [testPermissions, setTestPermissions] = useState({
    mcq_enabled: false,
    coding_enabled: false,
    behavioral_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const accessToken = sessionStorage.getItem("accessToken");
  const [limitModal, setLimitModal] = useState({ open: false, featureKey: null, errorCode: null });

  useEffect(() => {
    const checkAdminAuthorization = async () => {
      try {
        const response = await axios.get(`${host}/check_permissions_for_jd`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        setTestPermissions({
          mcq_enabled: response.data.mcq_enabled || false,
          coding_enabled: response.data.coding_enabled || false,
          behavioral_enabled: response.data.behavioral_enabled || false,
        });
      } catch (error) {
        console.error("Error checking admin ID:", error);
        if (error.response) {
          const { status, data } = error.response;
          if (status === 401) {
            toast.error("Unauthorized: Invalid token");
            sessionStorage.removeItem("accessToken");
          } else {
            toast.error(`Error: ${data.detail || "Failed to check admin status"}`);
          }
        } else {
          toast.error("Network error: Unable to reach the server");
        }
        setTestPermissions({
          mcq_enabled: false,
          coding_enabled: false,
          behavioral_enabled: false,
        });
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      checkAdminAuthorization();
    } else {
      setLoading(false);
    }
  }, [accessToken]);

  const testCategories = [
    {
      id: 1,
      title: 'MCQ Tests',
      type: 'mcq',
      icon: Brain,
      description: 'Multiple choice questions covering various technical topics, aptitude, and general knowledge.',
      action: () => navigate('/org-admin/mcqsets'),
      restricted: true,
    },
    {
      id: 2,
      title: 'Coding Tests',
      type: 'coding',
      icon: Code,
      description: 'Hands-on programming challenges to assess technical skills and problem-solving abilities.',
      action: () => navigate('/org-admin/createcodingproblemset'),
      restricted: true,
    },
    {
      id: 3,
      title: 'Behavioural Tests',
      type: 'behavioral',
      icon: Users,
      description: 'Scenario-based questions assessing communication, teamwork, and other core competencies.',
      action: () => navigate('/org-admin/behavioralsets'),
      restricted: true,
    },
  ];

  const CategoryCard = ({ category }) => {
    const IconComponent = category.icon;

    // Show loading state while authorization is being checked
    if (loading) {
      return (
        <div className="dark:glass-effect bg-bgSecondary rounded-2xl p-6 transition-all duration-300 hover:scale-105 hover:shadow-md hover:border-blue-300 cursor-pointer">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="p-4 rounded-full mb-4 text-gray-300">
              <IconComponent className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold text-[rgba(240,248,255,0.691)] mb-2">
              {category.title}
            </h3>
            <p className="text-[rgba(240,248,255,0.691)] text-sm mb-6">
              {category.description}
            </p>
            {/* Loading indicator */}
            <div className="flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
              <span className="ml-2 text-sm text-gray-400">Loading...</span>
            </div>
          </div>
        </div>
      );
    }

    // Check if this specific test type is enabled
    const isEnabled = testPermissions[`${category.type}_enabled`] || false;
    const isLocked = category.restricted && !isEnabled;

    const handleClick = () => {
      if (isLocked) {
        setLimitModal({ open: true, featureKey: `${category.type}_enabled`, errorCode: "FEATURE_LOCKED" });
        return;
      }
      category.action();
    };

    return (
      <div
        className={`dark:glass-effect bg-bgSecondary rounded-2xl p-6 transition-all duration-300 hover:scale-105 hover:shadow-md hover:border-blue-300 cursor-pointer relative
          ${isLocked ? 'opacity-70' : ''}`}
        onClick={handleClick}
      >
        {/* Beta badge for locked features */}
        {isLocked && (
          <div className="absolute top-3 right-3 z-10">
            <span className="px-2 py-1 text-xs font-medium bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-sm">
              Beta
            </span>
          </div>
        )}
        
        <div className="flex flex-col items-center text-center mb-6">
          <div className="p-4 rounded-full mb-4 text-textPrimary dark:text-gray-300">
            <IconComponent className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-semibold dark:text-[rgba(240,248,255,0.691)] text-textPrimary mb-2">
            {category.title}
            {isLocked && <span className="ml-2 text-blue-400 text-sm">β</span>}
          </h3>
          <p className="dark:text-[rgba(240,248,255,0.691)] text-textSecondary text-sm mb-6">
            {isLocked 
              ? 'Beta feature available on request. Enhance your assessment capabilities with advanced testing options.'
              : category.description
            }
          </p>
        </div>
      </div>
    );
  };

  // Show all categories instead of filtering
  const displayCategories = testCategories;

  return (
    <>
      <div className="min-h-screen">
        <div className="p-8 responsive-container">
          {loading ? (
          <Loader />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayCategories.map(category => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </div>
          )}
        </div>
      </div>
      <LimitExceededModal
        isOpen={limitModal.open}
        onClose={() => setLimitModal({ open: false, featureKey: null, errorCode: null })}
        featureKey={limitModal.featureKey}
        errorCode={limitModal.errorCode}
      />
    </>
  );
};

export default TestLibrary;